/**
 * Minimal real equirectangular panorama viewer — vanilla WebGL, no library.
 * A UV-sphere is generated with standard equirectangular texture coordinates
 * and rendered from a camera fixed at its center; drag changes yaw/pitch,
 * idle time resumes a slow auto-rotate. This is the same underlying idea the
 * real my360 viewer uses (photo sphere + orbit camera) — just hand-rolled in
 * plain WebGL instead of pulling in the full Babylon.js engine for a static
 * landing page.
 */
export interface PanoramaOptions {
  initialYaw?: number;
  initialPitch?: number;
  autoRotateSpeed?: number;
  idleDelayMs?: number;
  fov?: number;
  /** false turns the viewer into a directed backdrop (no drag handlers). */
  interactive?: boolean;
  onLoad?: () => void;
  onError?: (e: unknown) => void;
  onInteract?: () => void;
}

export interface Panorama {
  setYawPitch: (yaw: number, pitch: number) => void;
  getYawPitch: () => { yaw: number; pitch: number };
  setAutoRotate: (v: number) => void;
  setActive: (v: boolean) => void;
  setImage: (url: string, onDone?: () => void) => void;
  project: (u: number, v: number) => { x: number; y: number; visible: boolean };
  dispose: () => void;
}

export function createPanorama(
  canvas: HTMLCanvasElement,
  imageUrl: string,
  opts: PanoramaOptions = {},
): Panorama | null {
  var ctxOpts: WebGLContextAttributes = { antialias: true, alpha: false, powerPreference: 'low-power' };
  // Real equirectangular photos are never power-of-two sized, but WebGL1
  // forbids REPEAT wrap + mipmaps on NPOT textures (GL_INVALID_OPERATION on
  // generateMipmap). WebGL2 lifts that restriction entirely, so prefer it;
  // isWebGL2 below picks a safe CLAMP+no-mipmap fallback path on WebGL1.
  var ctx = canvas.getContext('webgl2', ctxOpts) as WebGLRenderingContext | null;
  var isWebGL2 = !!ctx;
  if (!ctx) ctx = canvas.getContext('webgl', ctxOpts) as WebGLRenderingContext | null;
  if (!ctx) return null;
  // const, so the non-null narrowing survives into every closure below —
  // a reassignable `var` loses it the moment it's captured.
  const gl = ctx;

  // ---- Shaders ----
  var vsSrc =
    'attribute vec3 aPosition;\n' +
    'attribute vec2 aUV;\n' +
    'uniform mat4 uProjection;\n' +
    'uniform mat4 uView;\n' +
    'varying vec2 vUV;\n' +
    'void main() {\n' +
    '  gl_Position = uProjection * uView * vec4(aPosition, 1.0);\n' +
    '  vUV = aUV;\n' +
    '}\n';
  var fsSrc =
    'precision mediump float;\n' +
    'varying vec2 vUV;\n' +
    'uniform sampler2D uTex;\n' +
    'void main() {\n' +
    '  gl_FragColor = texture2D(uTex, vUV);\n' +
    '}\n';

  function compile(type: number, src: string) {
    const s = gl.createShader(type);
    if (!s) return null;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error('panorama shader error:', gl.getShaderInfoLog(s));
      gl.deleteShader(s);
      return null;
    }
    return s;
  }
  var vs = compile(gl.VERTEX_SHADER, vsSrc);
  var fs = compile(gl.FRAGMENT_SHADER, fsSrc);
  if (!vs || !fs) return null;

  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('panorama link error:', gl.getProgramInfoLog(program));
    return null;
  }

  var aPosition = gl.getAttribLocation(program, 'aPosition');
  var aUV = gl.getAttribLocation(program, 'aUV');
  var uProjection = gl.getUniformLocation(program, 'uProjection');
  var uView = gl.getUniformLocation(program, 'uView');
  var uTex = gl.getUniformLocation(program, 'uTex');

  // ---- Sphere geometry with equirectangular UVs ----
  var LAT_BANDS = 40;
  var LON_BANDS = 40;
  var positions: number[] = [];
  var uvs: number[] = [];
  var indices: number[] = [];

  for (var lat = 0; lat <= LAT_BANDS; lat++) {
    var theta = (lat * Math.PI) / LAT_BANDS; // 0..PI (top..bottom)
    var sinTheta = Math.sin(theta);
    var cosTheta = Math.cos(theta);
    for (var lon = 0; lon <= LON_BANDS; lon++) {
      var phi = (lon * 2 * Math.PI) / LON_BANDS; // 0..2PI
      var sinPhi = Math.sin(phi);
      var cosPhi = Math.cos(phi);
      var x = cosPhi * sinTheta;
      var y = cosTheta;
      var z = sinPhi * sinTheta;
      positions.push(x, y, z);
      // Standard equirectangular mapping matching this parametrisation.
      uvs.push(1.0 - lon / LON_BANDS, lat / LAT_BANDS);
    }
  }
  for (lat = 0; lat < LAT_BANDS; lat++) {
    for (lon = 0; lon < LON_BANDS; lon++) {
      var first = lat * (LON_BANDS + 1) + lon;
      var second = first + LON_BANDS + 1;
      // Reversed winding vs. a normal outward sphere — camera sits INSIDE,
      // so the faces we want visible are the ones a standard sphere culls.
      indices.push(first, second, first + 1);
      indices.push(second, second + 1, first + 1);
    }
  }

  var posBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

  var uvBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(uvs), gl.STATIC_DRAW);

  var idxBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuffer);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);
  var indexCount = indices.length;

  // ---- Texture (loaded async; render starts once ready) ----
  var texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([18, 16, 12, 255]));
  var ready = false;

  /**
   * Loads (or swaps to) an equirect image. Also the public setImage() —
   * scene navigation in the scripted editor demo swaps textures this way.
   * onDone fires after the texture is live on the sphere.
   */
  function loadImage(url: string, onDone?: () => void) {
    var img: HTMLImageElement = new Image();
    img.onerror = function (e) {
      console.error('panorama: failed to load image', url, e);
      if (opts.onError) opts.onError(e);
    };
    img.onload = function () {
      // Real camera panoramas routinely exceed MAX_TEXTURE_SIZE (commonly
      // 4096–8192 depending on the GPU/driver) — texImage2D then fails
      // silently and the sphere renders the clear color forever, so oversized
      // sources are downscaled to a safe cap first. Decode + resize go through
      // createImageBitmap when available (off the main thread in Chromium —
      // the synchronous decode/drawImage path visibly stuttered the intro
      // animation), with the old canvas path kept as the fallback.
      var maxSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 4096;
      // 8192 cap: at fullscreen (~75° fov) a viewer only sees ~1/5 of the
      // equirect width, so 4096 textures looked soft on large screens.
      var cap = Math.min(maxSize, 8192);
      var needsResize = img.width > cap || img.height > cap;

      function canvasFallback(): TexImageSource {
        if (!needsResize) return img;
        var scale = cap / Math.max(img.width, img.height);
        var off = document.createElement('canvas');
        off.width = Math.round(img.width * scale);
        off.height = Math.round(img.height * scale);
        off.getContext('2d')!.drawImage(img, 0, 0, off.width, off.height);
        return off;
      }

      function finish(source: TexImageSource) {
        uploadTexture(source);
        if (onDone) onDone();
      }

      if ('createImageBitmap' in window) {
        var p;
        if (needsResize) {
          var scale = cap / Math.max(img.width, img.height);
          p = createImageBitmap(img, {
            resizeWidth: Math.round(img.width * scale),
            resizeHeight: Math.round(img.height * scale),
            resizeQuality: 'high',
          });
        } else {
          p = createImageBitmap(img);
        }
        p.then(finish).catch(function () { finish(canvasFallback()); });
      } else {
        finish(canvasFallback());
      }
    };
    img.decoding = 'async';
    img.src = url;
  }

  function uploadTexture(source: TexImageSource) {
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    // WEBGL_S wraps (REPEAT) so dragging past the seam continues seamlessly
    // into the other side of the photo, exactly like the real photo's left
    // and right edges already match. WebGL2 allows this + mipmaps on any
    // size texture; WebGL1 only allows REPEAT/mipmaps on power-of-two
    // textures, which a real camera photo never is — so on that fallback
    // path we drop mipmapping instead of crashing on generateMipmap().
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (isWebGL2) {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.generateMipmap(gl.TEXTURE_2D);
    } else {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    }
    ready = true;
    if (opts.onLoad) opts.onLoad();
  }
  loadImage(imageUrl);

  // ---- Minimal 4x4 matrix helpers (column-major, like WebGL expects) ----
  function perspective(fovy: number, aspect: number, near: number, far: number) {
    var f = 1.0 / Math.tan(fovy / 2);
    var nf = 1 / (near - far);
    return new Float32Array([
      f / aspect, 0, 0, 0,
      0, f, 0, 0,
      0, 0, (far + near) * nf, -1,
      0, 0, 2 * far * near * nf, 0,
    ]);
  }
  function viewFromYawPitch(yaw: number, pitch: number) {
    var cy = Math.cos(yaw), sy = Math.sin(yaw);
    var cp = Math.cos(pitch), sp = Math.sin(pitch);
    // Rotation = Rx(pitch) * Ry(yaw), then used directly as the view matrix
    // (camera at the origin looking down -Z; rotating the world is
    // equivalent to rotating the camera the opposite way for our purposes
    // since we only ever orbit, never translate).
    var r00 = cy, r01 = 0, r02 = sy;
    var r10 = sy * sp, r11 = cp, r12 = -cy * sp;
    var r20 = -sy * cp, r21 = sp, r22 = cy * cp;
    return new Float32Array([
      r00, r10, r20, 0,
      r01, r11, r21, 0,
      r02, r12, r22, 0,
      0, 0, 0, 1,
    ]);
  }

  // ---- Orbit state ----
  var yaw = opts.initialYaw || 0;
  var pitch = opts.initialPitch || 0;
  var autoRotateSpeed = opts.autoRotateSpeed != null ? opts.autoRotateSpeed : 0.0009;
  var idleSince = performance.now();
  var idleDelayMs = opts.idleDelayMs != null ? opts.idleDelayMs : 1800;
  var dragging = false;
  var lastX = 0, lastY = 0;

  function pointerDown(e: PointerEvent | TouchEvent) {
    dragging = true;
    idleSince = performance.now();
    var p: { clientX: number; clientY: number } = 'touches' in e ? e.touches[0] : e;
    lastX = p.clientX; lastY = p.clientY;
  }
  function pointerMove(e: PointerEvent | TouchEvent) {
    if (!dragging) return;
    var p: { clientX: number; clientY: number } = 'touches' in e ? e.touches[0] : e;
    var dx = p.clientX - lastX;
    var dy = p.clientY - lastY;
    lastX = p.clientX; lastY = p.clientY;
    yaw -= dx * 0.0045;
    pitch = Math.max(-1.35, Math.min(1.35, pitch - dy * 0.0045));
    idleSince = performance.now();
    if (opts.onInteract) opts.onInteract();
  }
  function pointerUp() { dragging = false; idleSince = performance.now(); }

  // interactive:false turns the viewer into a directed backdrop — no drag
  // handlers at all. Used where WE drive the camera (hero ambiance, the
  // scripted hotspot scene) and visitor input would fight the choreography.
  var interactive = opts.interactive !== false;
  if (interactive) {
    canvas.addEventListener('pointerdown', pointerDown);
    window.addEventListener('pointermove', pointerMove);
    window.addEventListener('pointerup', pointerUp);
    canvas.addEventListener('touchstart', pointerDown, { passive: true });
    window.addEventListener('touchmove', pointerMove, { passive: true });
    window.addEventListener('touchend', pointerUp);
  }

  // ---- Render loop ----
  var raf: number | null = null;
  var disposed = false;
  var active = true;
  var lastFrame = performance.now();

  function resize() {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = canvas.clientWidth * dpr;
    var h = canvas.clientHeight * dpr;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
    }
  }

  gl.enable(gl.DEPTH_TEST);
  gl.clearColor(0.07, 0.06, 0.05, 1);

  function frame(now: number) {
    if (disposed) return;
    // Inactive (offscreen) viewers keep the rAF alive but skip all GL work —
    // several skyboxes coexist on the page and only visible ones may cost.
    if (!active) {
      lastFrame = now;
      raf = requestAnimationFrame(frame);
      return;
    }
    var dt = now - lastFrame;
    lastFrame = now;
    if (!dragging && now - idleSince > idleDelayMs) {
      yaw += autoRotateSpeed * dt;
    }
    resize();
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    if (ready) {
      gl.useProgram(program);

      gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
      gl.enableVertexAttribArray(aPosition);
      gl.vertexAttribPointer(aPosition, 3, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
      gl.enableVertexAttribArray(aUV);
      gl.vertexAttribPointer(aUV, 2, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuffer);

      var aspect = canvas.width / Math.max(1, canvas.height);
      gl.uniformMatrix4fv(uProjection, false, perspective((opts.fov || 75) * Math.PI / 180, aspect, 0.1, 10));
      gl.uniformMatrix4fv(uView, false, viewFromYawPitch(yaw, pitch));

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(uTex, 0);

      gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_SHORT, 0);
    }

    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  return {
    setYawPitch: function (y: number, p: number) { yaw = y; pitch = p; },
    getYawPitch: function () { return { yaw: yaw, pitch: pitch }; },
    setAutoRotate: function (v: number) { autoRotateSpeed = v; },
    setActive: function (v: boolean) { active = !!v; },
    setImage: function (url: string, onDone?: () => void) { loadImage(url, onDone); },
    /**
     * Projects an equirect image position (u,v — fractions, u left→right,
     * v top→bottom, same convention the AI hotspot data uses) into CSS
     * pixel coordinates on the canvas for the CURRENT camera. Returns
     * { x, y, visible } — visible=false when the point is behind the
     * camera. This is what anchors DOM hotspot markers onto the 3D world.
     */
    project: function (u: number, v: number) {
      // Inverse of the sphere's UV mapping (uv u = 1 - φ/2π, v = θ/π).
      var phi = 2 * Math.PI * (1 - u);
      var theta = Math.PI * v;
      var d = [
        Math.cos(phi) * Math.sin(theta),
        Math.cos(theta),
        Math.sin(phi) * Math.sin(theta),
      ];
      // Same rotation the renderer uses (world→camera), row-major applied.
      var cy = Math.cos(yaw), sy = Math.sin(yaw);
      var cp = Math.cos(pitch), sp = Math.sin(pitch);
      var cx0 = cy * d[0] + 0 * d[1] + sy * d[2];
      var cy0 = sy * sp * d[0] + cp * d[1] - cy * sp * d[2];
      var cz0 = -sy * cp * d[0] + sp * d[1] + cy * cp * d[2];
      if (cz0 >= -0.02) return { x: 0, y: 0, visible: false };
      var f = 1 / Math.tan(((opts.fov || 75) * Math.PI / 180) / 2);
      var aspect = canvas.clientWidth / Math.max(1, canvas.clientHeight);
      var ndcX = (f / aspect) * cx0 / -cz0;
      var ndcY = f * cy0 / -cz0;
      return {
        x: (ndcX * 0.5 + 0.5) * canvas.clientWidth,
        y: (1 - (ndcY * 0.5 + 0.5)) * canvas.clientHeight,
        visible: Math.abs(ndcX) < 1.15 && Math.abs(ndcY) < 1.15,
      };
    },
    dispose: function () {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      if (interactive) {
        canvas.removeEventListener('pointerdown', pointerDown);
        window.removeEventListener('pointermove', pointerMove);
        window.removeEventListener('pointerup', pointerUp);
      }
      gl.deleteProgram(program);
      gl.deleteBuffer(posBuffer);
      gl.deleteBuffer(uvBuffer);
      gl.deleteBuffer(idxBuffer);
      gl.deleteTexture(texture);
    },
  };
}
